import { useState, useEffect } from 'react';
import { Check, Loader2, AlertCircle, Tag, Info } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { useAuthStore } from '@/stores/authStore';
import { usePaymentCheckout } from '@/hooks/usePaymentCheckout';
import { getProviderDisplayName } from '@/lib/paymentProvider';
import type { SubscriptionPlan } from '@/lib/api';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedPlan?: string;
  billingPeriod?: 'monthly' | 'annual';
}

// Original prices for partner discount calculation
const ORIGINAL_PRICES: Record<string, number> = { basic: 69, pro: 99, scale: 179 };

export default function PaymentModal({ isOpen, onClose, onSuccess, preselectedPlan, billingPeriod: initialBillingPeriod = 'monthly' }: PaymentModalProps) {
  const { plans, fetchPlans, createCheckout } = useSubscriptionStore();
  const user = useAuthStore((s) => s.user);
  const partner = user?.partner;
  const { openCheckout } = usePaymentCheckout({
    onCheckoutComplete: onSuccess,
  });

  const [selectedPlan, setSelectedPlan] = useState<string | null>(preselectedPlan || null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>(partner ? 'monthly' : initialBillingPeriod);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isOpen && plans.length === 0) {
      fetchPlans();
    }
  }, [isOpen, plans.length, fetchPlans]);

  useEffect(() => {
    if (preselectedPlan) {
      setSelectedPlan(preselectedPlan);
    }
  }, [preselectedPlan]);

  // Force monthly for partner users
  useEffect(() => {
    if (partner) {
      setBillingPeriod('monthly');
    }
  }, [partner]);

  const getPartnerPrice = (planId: string): number | null => {
    if (!partner) return null;
    const originalPrice = ORIGINAL_PRICES[planId];
    if (!originalPrice) return null;
    return Math.round(originalPrice * (1 - partner.discount_percentage / 100) * 100) / 100;
  };

  const isPlanAllowed = (planId: string): boolean => {
    if (!partner) return true;
    if (!partner.allowed_plans || partner.allowed_plans.length === 0) return true;
    return partner.allowed_plans.includes(planId.toLowerCase());
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    setError(null);
  };

  const needsSetupFee = user?.setup_fee_status === 'pending';

  const handleSetupFeeCheckout = async () => {
    setError(null);
    setProcessing(true);
    try {
      const { subscriptionApi } = await import('@/lib/api');
      const setupResult = await subscriptionApi.createSetupFeeCheckout();
      const checkoutUrl = setupResult.data.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear el checkout');
    } finally {
      setProcessing(false);
    }
  };

  const handleProceedToCheckout = async () => {
    if (!selectedPlan) return;
    setError(null);
    setProcessing(true);

    try {
      const result = await createCheckout(selectedPlan, billingPeriod);

      if (result.success && result.data) {
        await openCheckout(result.data);
        onClose();
      } else {
        setError(result.error || 'Error al iniciar el checkout');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setProcessing(false);
    }
  };

  const filteredPlans = partner
    ? plans.filter((plan) => isPlanAllowed(plan.id))
    : plans;

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && onClose()} title={needsSetupFee ? 'Cuota de alta' : 'Selecciona tu plan'} size="md">
          {/* Setup fee step */}
          {needsSetupFee && (
            <div className="flex gap-3 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-semibold mb-1">Paso 1 de 2: Cuota de alta única</p>
                <p className="text-amber-700">
                  Para activar tu cuenta, primero se cobra una cuota de alta de <strong>100€</strong> (pago único).
                  Después podrás elegir y suscribirte al plan que prefieras.
                </p>
              </div>
            </div>
          )}

          {/* Partner discount banner */}
          {partner && !needsSetupFee && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              <Tag className="w-4 h-4 flex-shrink-0" />
              <span>
                <strong>{partner.discount_percentage}% de descuento</strong> aplicado automáticamente vía {partner.name}
              </span>
            </div>
          )}

          {/* Billing Period Toggle — hidden for partner users and setup fee step */}
          {!partner && !needsSetupFee && (
            <div className="flex items-center justify-center gap-2 mb-6 p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Anual
                <span className="ml-1 text-xs text-green-600 font-semibold">-15%</span>
              </button>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Plans — greyed out during setup fee step */}
          <div className={`space-y-4 ${needsSetupFee ? 'opacity-40 pointer-events-none' : ''}`}>
            {needsSetupFee && (
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Paso 2: Elige tu plan (después del alta)</p>
            )}
            {filteredPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingPeriod={billingPeriod}
                isSelected={selectedPlan === plan.id}
                isProcessing={false}
                onSelect={() => handlePlanSelect(plan.id)}
                disabled={processing || needsSetupFee}
                partnerPrice={getPartnerPrice(plan.id)}
                partnerName={partner?.name}
              />
            ))}
          </div>

          {needsSetupFee ? (
            <button
              onClick={handleSetupFeeCheckout}
              disabled={processing}
              className={`mt-6 w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                !processing
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparando checkout...
                </>
              ) : (
                'Pagar cuota de alta (100€) →'
              )}
            </button>
          ) : (
            <button
              onClick={handleProceedToCheckout}
              disabled={!selectedPlan || processing}
              className={`mt-6 w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                selectedPlan && !processing
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparando checkout...
                </>
              ) : (
                'Continuar con el pago →'
              )}
            </button>
          )}

          <p className="mt-4 text-xs text-slate-500 text-center">
            El pago se procesa de forma segura a través de {getProviderDisplayName()}.
            Puedes cancelar en cualquier momento.
          </p>
    </Modal>
  );
}

function PlanCard({
  plan,
  billingPeriod,
  isSelected,
  isProcessing,
  onSelect,
  disabled,
  partnerPrice,
  partnerName,
}: {
  plan: SubscriptionPlan;
  billingPeriod: 'monthly' | 'annual';
  isSelected: boolean;
  isProcessing: boolean;
  onSelect: () => void;
  disabled: boolean;
  partnerPrice?: number | null;
  partnerName?: string;
}) {
  const popular = plan.id === 'pro';
  const PLAN_PRICES: Record<string, number> = { basic: 49, pro: 79, scale: 149 };
  const monthlyPrice = plan.original_price || PLAN_PRICES[plan.id] || plan.price;

  // Calculate annual price with 15% discount
  const annualMonthlyPrice = Math.round(monthlyPrice * 0.85);
  const annualTotalPrice = annualMonthlyPrice * 12;

  const hasPartnerDiscount = partnerPrice !== null && partnerPrice !== undefined;
  const displayPrice = hasPartnerDiscount
    ? partnerPrice
    : billingPeriod === 'annual' ? annualMonthlyPrice : monthlyPrice;
  const priceLabel = hasPartnerDiscount
    ? '/mes'
    : billingPeriod === 'annual' ? `/mes (${annualTotalPrice}€/año)` : '/mes';

  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-slate-200 hover:border-slate-300'
      } ${popular ? 'relative' : ''} ${disabled ? 'opacity-75' : ''}`}
    >
      {popular && (
        <span className="absolute -top-3 left-4 px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded">
          Popular
        </span>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">{plan.name}</h3>
          <p className="text-sm text-slate-500">
            {plan.users} usuario{plan.users > 1 ? 's' : ''} · {plan.ocr_scans === -1 ? 'OCR ilimitado' : `${plan.ocr_scans} escaneos OCR/mes`}
          </p>
        </div>
        <div className="text-right">
          {hasPartnerDiscount ? (
            <>
              <p className="text-sm text-slate-400 line-through">{monthlyPrice}€</p>
              <p className="text-2xl font-bold text-green-600">{displayPrice}€</p>
            </>
          ) : (
            <p className="text-2xl font-bold text-slate-900">{displayPrice}€</p>
          )}
          <p className="text-sm text-slate-500">{priceLabel}</p>
        </div>
      </div>

      {hasPartnerDiscount && partnerName && (
        <div className="mt-2 flex items-center gap-1.5">
          <Tag className="w-3 h-3 text-green-600" />
          <span className="text-xs text-green-700 font-medium">Precio exclusivo {partnerName}</span>
        </div>
      )}

      {isProcessing && (
        <div className="mt-3 pt-3 border-t border-blue-200">
          <div className="flex items-center gap-2 text-blue-600 text-sm font-medium">
            <Loader2 className="w-4 h-4 animate-spin" />
            Preparando checkout...
          </div>
        </div>
      )}

      {isSelected && !isProcessing && (
        <div className="mt-3 pt-3 border-t border-blue-200">
          <div className="flex items-center gap-2 text-blue-600 text-sm font-medium">
            <Check className="w-4 h-4" />
            Plan seleccionado
          </div>
        </div>
      )}
    </button>
  );
}
